import React, { useEffect, useMemo, useState } from "react";
import { useApp, t } from "@/lib/AppContext";
import { api, resolveImg, BODY_LABELS, FUEL_LABELS, TRANSMISSION_LABELS } from "@/lib/api";
import CarCard from "@/components/CarCard";
import SectionLabel from "@/components/SectionLabel";
import { Filter, X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Catalog() {
  const { content, settings } = useApp();
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    brand: "", body_type: "", fuel_type: "", transmission: "", country_origin: "", customs_cleared: "", euro_standard: "",
  });
  const PER_PAGE = 12;

  useEffect(() => { api.get("/configurator-options").then(r => setOptions(r.data || [])).catch(() => {}); }, []);

  const fetchCars = (p = page) => {
    setLoading(true);
    const params = { page: p, per_page: PER_PAGE };
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      if (k === "customs_cleared") params[k] = v === "yes";
      else params[k] = v;
    });
    api.get("/cars", { params })
      .then((r) => { setCars(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCars(page); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => { setPage(1); fetchCars(1); /* eslint-disable-next-line */ }, [filters]);

  const byType = useMemo(() => {
    const map = { brand: [], body: [], fuel: [], transmission: [], country: [], customs: [], euro: [] };
    options.forEach(o => { if (map[o.type]) map[o.type].push(o); });
    return map;
  }, [options]);

  const reset = () => setFilters({ brand: "", body_type: "", fuel_type: "", transmission: "", country_origin: "", customs_cleared: "", euro_standard: "" });
  const hasFilters = Object.values(filters).some(v => v);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const bg = settings?.bg_image_urls?.catalog;

  const FilterSection = ({ title, value, onChange, items, mapLabel }) => (
    <div className="mb-5">
      <h4 className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)] mb-2">{title}</h4>
      <div className="flex flex-wrap gap-2">
        {items.map(it => {
          const v = it.value;
          const label = mapLabel ? mapLabel(it) : it.label || v;
          const active = value === v;
          return (
            <button key={v} onClick={() => onChange(active ? "" : v)}
              className="px-3 py-1.5 rounded-full text-xs font-accent uppercase tracking-wider transition-colors"
              style={{
                background: active ? "var(--color-secondary)" : "transparent",
                color: active ? "#FFFFFF" : "var(--color-text-secondary)",
                border: `1.5px solid ${active ? "var(--color-secondary)" : "var(--color-border)"}`,
              }}
              data-testid={`filter-${title}-${v}`}>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div data-testid="catalog-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24 section-on-dark">
        <div className="absolute inset-0">
          {bg && <img src={resolveImg(bg)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: "rgba(28,58,107,0.78)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionLabel light>{t(content, "catalog.label", "")}</SectionLabel>
          <h1 className="section-heading text-white mt-4">{t(content, "catalog.title", "Каталог")}</h1>
          <p className="text-white/75 mt-3 max-w-2xl">{t(content, "catalog.subtitle", "")}</p>
        </div>
      </section>

      {/* Body */}
      <section className="py-10 md:py-14 bg-[var(--color-bg-primary)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[300px_1fr] gap-8">
          {/* Filters */}
          <aside className={`bg-white rounded-[var(--radius-lg)] p-5 border h-fit lg:sticky lg:top-24 ${filtersOpen ? "fixed inset-4 z-50 overflow-y-auto" : "hidden lg:block"}`} style={{ borderColor: "var(--color-border)" }} data-testid="catalog-filters">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-headings font-bold text-lg">Фильтры</h3>
              <div className="flex gap-2">
                {hasFilters && (
                  <button onClick={reset} className="text-xs font-accent uppercase tracking-wider text-[var(--color-primary)] hover:underline" data-testid="filter-reset">
                    Сбросить
                  </button>
                )}
                <button onClick={() => setFiltersOpen(false)} className="lg:hidden text-[var(--color-text-secondary)]" data-testid="filters-close">
                  <X size={20} />
                </button>
              </div>
            </div>
            <FilterSection title="Марка" value={filters.brand} onChange={(v) => setFilters(f => ({...f, brand: v}))} items={byType.brand} />
            <FilterSection title="Кузов" value={filters.body_type} onChange={(v) => setFilters(f => ({...f, body_type: v}))} items={byType.body} />
            <FilterSection title="Топливо" value={filters.fuel_type} onChange={(v) => setFilters(f => ({...f, fuel_type: v}))} items={byType.fuel} />
            <FilterSection title="Коробка" value={filters.transmission} onChange={(v) => setFilters(f => ({...f, transmission: v}))} items={byType.transmission} />
            <FilterSection title="Страна" value={filters.country_origin} onChange={(v) => setFilters(f => ({...f, country_origin: v}))} items={byType.country} />
            <FilterSection title="Растаможен" value={filters.customs_cleared} onChange={(v) => setFilters(f => ({...f, customs_cleared: v}))} items={byType.customs} />
            <FilterSection title="Евро" value={filters.euro_standard} onChange={(v) => setFilters(f => ({...f, euro_standard: v}))} items={byType.euro} />
          </aside>

          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-[var(--color-text-secondary)]" data-testid="catalog-total">
                Найдено: <span className="font-headings font-bold text-[var(--color-text-primary)]">{total}</span>
              </div>
              <button onClick={() => setFiltersOpen(true)} className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-md font-accent text-sm uppercase tracking-wider text-white" style={{ background: "var(--color-secondary)" }} data-testid="filters-open">
                <Filter size={16} /> Фильтры
              </button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1,2,3,4,5,6].map((i) => <div key={i} className="skeleton h-72" />)}
              </div>
            ) : cars.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-[var(--radius-lg)]">
                <div className="text-[var(--color-text-secondary)] mb-4">Авто по выбранным параметрам не найдено</div>
                {hasFilters && <button onClick={reset} className="btn-secondary-cta">Сбросить фильтры</button>}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {cars.map(c => <CarCard key={c.id} car={c} />)}
                </div>
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-10" data-testid="pagination">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-2 rounded-md border disabled:opacity-40" style={{ borderColor: "var(--color-border)" }}>
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({length: totalPages}, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => setPage(p)} className="w-10 h-10 rounded-md font-accent font-semibold text-sm transition-colors" style={{
                        background: p === page ? "var(--color-secondary)" : "transparent",
                        color: p === page ? "#FFFFFF" : "var(--color-text-primary)",
                        border: `1.5px solid ${p === page ? "var(--color-secondary)" : "var(--color-border)"}`,
                      }}>{p}</button>
                    ))}
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-2 rounded-md border disabled:opacity-40" style={{ borderColor: "var(--color-border)" }}>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
